import Fields from './Fields'
import useTranslations from "../../hooks/useTranslations";
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { QuestionnaireContext } from '../../StateManagment/Questionnaire';
import { Responses } from '../../StateManagment/Responses';
import sortSectionFields from '../../helpers/questionnaire/sortSectionFields';
import notDeleted from '../../helpers/notDeleted';
import saveQuestionnaireResponses from './saveQuestionnaireResponses';
import hiddenResponsesScope from './hiddenResponsesScope';
import callSaveQuestionnaireResponse from './callSaveQuestionnaireResponse';
import { timestamp } from '../../firebase/config';
import { firstRequiredBlock } from '../../helpers/questionnaires/requiredFieldsBlock';
import requiredBlockMessage from './requiredBlockMessage';
import loadMatrixRows from './loadMatrixRows';

// Grouped section view (gamification OFF): shows all of a section's questions on one
// page. Gamification ON flattens section questions into individual steps upstream in
// QuestionnaireOpen, so this component is only used when gamification is off.
/**
 * @param {object} props
 * @param {object} props.field - The section's QuestionnaireFields doc (Type 'section')
 * @param {number} props.index - This section's step index in the navigation
 * @param {(i: number) => void} props.setFieldIndex - Advances/rewinds the current step
 * @param {number} props.fieldIndex - Current step index
 * @param {string} props.compagnyId - Tenant CompagnyID
 * @param {object} props.questionaire - Parent Questionnaires doc
 * @param {string} props.languageCode - Active language code (e.g. 'nl')
 * @param {Array<object>} props.allFields - All QuestionnaireFields of the questionnaire
 * @param {string} props.moment - MomentID url param
 * @param {string} props.persona - PersonaID url param
 * @param {string} props.questionnaireId - Questionnaire ID url param
 * @param {string} props.research - ResearchID url param
 * @param {string} props.sessionId - Respondent session id
 * @param {Array<string>} [props.hiddenFieldIDs] - Vragen die door een dichtgeklapte
 *   tak verborgen raakten; hun antwoorden worden bij het verdergaan opgeruimd
 */
const Section = ({field, index, setFieldIndex, fieldIndex, compagnyId, questionaire, languageCode, allFields, moment, persona, questionnaireId, research, sessionId, hiddenFieldIDs}) => {

    // Derive this section's questions from the fields the parent already loaded — no
    // extra Firestore listener. Sorted client-side by SectionPosition (Position
    // fallback); ordering by SectionPosition in Firestore would drop un-migrated docs.
    //
    // `allFields` is AL op zichtbaarheid gefilterd door QuestionnaireOpen. Filter
    // hier niet nog eens op ShowWhen: de triggervraag kan buiten deze sectie
    // staan, en een lokaal filter zou hem dan als 'bestaat niet' zien en
    // fail-open toepassen — de vraag bleef dan altijd staan.
    // AI-CONTEXT: See docs/features/conditional-questions.md
    const sectionFields = useMemo(
        () => sortSectionFields((allFields || []).filter(f => f.SectionID === field.ID && notDeleted(f))),
        [allFields, field.ID]
    )

    const { isTransitioning, settings } = useContext(QuestionnaireContext);
    const [responses] = useContext(Responses);

    // Waarom de respondent blijft staan, en bij welke vraag; leeg zolang er
    // niets aan de hand is. De melding staat onder díe vraag en komt in beeld:
    // onderaan de sectie stond hij buiten beeld en leek 'Volgende' niets te
    // doen (testronde 2026-10-28, B2).
    const [blockMessage, setBlockMessage] = useState('');
    const [blockedFieldID, setBlockedFieldID] = useState('');
    /** @type {React.RefObject<HTMLParagraphElement>} */
    const blockRef = useRef(null);

    useEffect(() => {
        if (blockMessage && blockRef.current) {
            blockRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [blockMessage, blockedFieldID]);
    const [checking, setChecking] = useState(false);
    // Tijdens het opslaan: knoppen uit en 'Bezig…', net als bij een losse vraag
    // (TopLevelFields). Anders startte een tweede klik een tweede opslag en zag
    // de respondent niets gebeuren (testronde 2026-10-13, B14).
    const [saving, setSaving] = useState(false);

    // Persist every answered question in the section, then advance to the next step.
    // (The old Section step advanced without saving — section answers were lost.)
    // AI-CONTEXT: See docs/pitfalls.md#state-nav-step-advances-without-saving
    const saveSectionAndAdvance = async () => {
        if (checking || saving) return;
        setBlockMessage('');
        setBlockedFieldID('');

        // Verplichte vragen gelden hier net zo goed. Een sectie toont meerdere
        // vragen op één pagina, dus de poort kijkt naar ALLE vragen van de
        // sectie — niet alleen de eerste. Dezelfde poort als de losse vraag
        // gebruikt; zonder die deling loopt er één van de twee achter, en dat
        // is precies wat hier gebeurd was: het sterretje stond er wel, maar
        // 'Volgende' liet iedereen door.
        // AI-CONTEXT: See docs/pitfalls.md#ui-shared-affordance-gated-per-caller
        // AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
        setChecking(true);
        let blokkade;
        try {
            blokkade = await firstRequiredBlock(sectionFields, responses, loadMatrixRows);
        } finally {
            setChecking(false);
        }
        if (blokkade) {
            setBlockMessage(requiredBlockMessage(blokkade.reason, translations));
            setBlockedFieldID(blokkade.fieldID);
            return;
        }

        const answered = sectionFields
            .map(child => ({ child, childResponses: responses.filter(r => r.FieldID === child.ID) }))
            .filter(({ childResponses }) => childResponses.length > 0)

        setSaving(true);
        try {
            // Deze stap doet één aanroep per beantwoorde vraag. De opruiming van
            // weesantwoorden hoort maar één keer mee te gaan — bij de eerste, zodat
            // ze in dezelfde batch zit als een echte schrijfactie.
            // AI-CONTEXT: See docs/features/conditional-questions.md
            await Promise.all(
                answered.map(({ child, childResponses }, i) => saveQuestionnaireResponses({
                        field: child,
                        fieldResponses: childResponses,
                        position: index,
                        questionnaireId,
                        moment,
                        research,
                        persona,
                        sessionId,
                        hiddenFieldIDs: i === 0 ? hiddenFieldIDs : [],
                        addressStep: settings?.addressStep,
                        nameStep: settings?.nameStep,
                    }))
            );

            // Niemand in deze sectie antwoordde, dus er ging geen enkele aanroep uit:
            // de opruiming heeft dan een eigen aanroep nodig.
            const hiddenScope = answered.length === 0
                ? hiddenResponsesScope({ hiddenFieldIDs, questionnaireId, moment, research, sessionId })
                : undefined
            if (hiddenScope) {
                await callSaveQuestionnaireResponse({
                    timestampMillis: timestamp.toMillis(),
                    responses: [],
                    hidden: hiddenScope,
                })
            }

            setFieldIndex(index + 1);
        } finally {
            setSaving(false);
        }
    };

    // Translations
    const stringsToTranslate = useMemo(() => {
      if (!sectionFields) return {};
  
      return {
          "Terug": "Terug",
          "Volgende": "Volgende",
          "Bezig": "Bezig…",
          ...(sectionFields ? Object.fromEntries(sectionFields.map(field => [field.Question, field.Question])) : {}),
      };
    }, [sectionFields]);

    const { translations, loading } = useTranslations(stringsToTranslate, languageCode ? languageCode : 'nl');

  return (
    <div id='section-outer-container' style={{display: index !== fieldIndex ? 'none' : 'block' }}>
      <div id='section-container' style={{borderLeft: `5px solid ${field.Color}`, '--qo-section-color': field.Color, display: index !== fieldIndex ? 'none' : 'flex' }} >
          <h2>{translations?.[field.Question] || field.Question}</h2>
          {sectionFields && sectionFields.map((field, index) => (
              <Fragment key={field.ID}>
                  <Fields
                  field={field}
                  compagnyId={compagnyId}
                  questionaire={questionaire}
                  languageCode={languageCode}
                  isVisible={index === fieldIndex}
                  questionNumber={index + 1}
                  questionTotal={sectionFields.length}
                  />
                  {blockMessage && field.ID === blockedFieldID && (
                    <p ref={blockRef} className="questionnaire-rows-required" role="alert">
                      {blockMessage}
                    </p>
                  )}
              </Fragment>
          ))}
          
      </div>
      <div className="questionnaire-button-container">
        <button disabled={isTransitioning || checking || saving} onClick={() => setFieldIndex(fieldIndex - 1)}>{translations?.["Terug"] || "Terug"}</button>
        <button disabled={isTransitioning || checking || saving} onClick={saveSectionAndAdvance}>
          {saving ? <span role="status">{translations?.["Bezig"] || "Bezig…"}</span> : (translations?.["Volgende"] || "Volgende")}
        </button>
      </div>
    </div>
  )
}

export default Section